import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, Alert, Button, LinearProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { styled, keyframes } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useDropzone } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ReportRenderer from '../components/Report/ReportRenderer';
import { TFunction } from 'i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { DESIGN_TOKENS } from '../theme/designTokens';

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const UploadSection = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
  [theme.breakpoints.up('lg')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.xl),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  },
  [theme.breakpoints.between('sm', 'md')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.md),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
  },
}));

const DropzoneContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(8),
  textAlign: 'center',
  cursor: 'pointer',
  border: '2px dashed #D1D5DB',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  background: '#FEFEFE',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  maxWidth: '800px',
  margin: '0 auto',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(90deg, transparent, rgba(94, 106, 210, 0.04), transparent)',
    transform: 'translateX(-100%)',
    transition: 'transform 0.6s',
  },
  
  '&:hover': {
    borderColor: '#4338CA',
    background: '#FEFEFE',
    transform: 'translateY(-2px)',
    '& .upload-icon': {
      color: '#4338CA',
      transform: 'scale(1.05)',
      animation: `${pulse} 2s ease-in-out infinite`,
    },
    '&::before': {
      transform: 'translateX(100%)',
    },
  },

  '&.drag-active': {
    borderColor: '#5E6AD2',
    background: 'rgba(94, 106, 210, 0.02)',
    '& .upload-icon': {
      color: '#5E6AD2',
      transform: 'scale(1.1)',
      animation: `${pulse} 1s ease-in-out infinite`,
    },
  },

  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(10),
    borderRadius: theme.spacing(4),
    '& .upload-icon': {
      fontSize: '4rem',
    },
    '& .upload-text': {
      fontSize: '1.75rem',
    },
  },

  [theme.breakpoints.between('sm', 'md')]: {
    padding: theme.spacing(6),
    borderRadius: theme.spacing(3),
    '& .upload-icon': {
      fontSize: '3.5rem',
    },
    '& .upload-text': {
      fontSize: '1.5rem',
    },
  },

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    '& .upload-icon': {
      fontSize: '3rem',
    },
    '& .upload-text': {
      fontSize: '1.25rem',
    },
  },
}));

const ProgressContainer = styled(motion.div)(({ theme }) => ({
  marginTop: theme.spacing(4),
  textAlign: 'center',
  padding: theme.spacing(4),
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.08) 0%, rgba(109, 40, 217, 0.08) 100%)',
  backgroundColor: 'rgba(254, 254, 254, 0.9)',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  border: '1px solid rgba(67, 56, 202, 0.2)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  position: 'relative',
  overflow: 'hidden',
  
  // Safari fallback - more opaque background when backdrop-filter isn't supported
  '@supports not (backdrop-filter: blur(10px))': {
    backgroundColor: 'rgba(254, 254, 254, 0.95)',
    background: 'linear-gradient(135deg, rgba(94, 106, 210, 0.12) 0%, rgba(139, 92, 246, 0.12) 100%)',
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(
      90deg,
      transparent,
      rgba(94, 106, 210, 0.1),
      transparent
    )`,
    animation: `${shimmer} 2s infinite linear`,
  },
}));



const DownloadButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '1rem',
  padding: theme.spacing(1.5, 4),
  borderRadius: DESIGN_TOKENS.borderRadius.sm,
  textTransform: 'none',
  boxShadow: DESIGN_TOKENS.shadows.button.default,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s',
  },
  
  '&:hover': {
    background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)',
    
    '&::before': {
      left: '100%',
    },
  },
  
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const ReportContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  marginTop: theme.spacing(6),
  background: '#FEFEFE !important',
  backgroundColor: '#FEFEFE !important',
  borderRadius: theme.spacing(3),
  border: '1px solid #E5E7EB',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  color: '#111827',
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  
  // Safari mobile compatibility fixes
  WebkitBackfaceVisibility: 'hidden',
  WebkitTransform: 'translate3d(0, 0, 0)',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(6),
    maxWidth: '1200px',
    margin: `${theme.spacing(6)} auto 0`,
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
    margin: `${theme.spacing(4)} auto 0`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    margin: `${theme.spacing(3)} auto 0`,
  },
  
  // Force white background on all child elements for Safari compatibility
  '& *': {
    color: 'inherit !important',
    backgroundColor: 'transparent !important',
  },
  
  // Override markdown preview styles for Safari
  '& .w-md-editor-preview': {
    backgroundColor: '#FEFEFE !important',
    color: '#111827 !important',
  },
  
  '& .wmde-markdown': {
    backgroundColor: '#FEFEFE !important',
    color: '#111827 !important',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important',
  },
  
  '& .wmde-markdown *': {
    backgroundColor: 'transparent !important',
    color: 'inherit !important',
  },
  
  '& .wmde-markdown h1, & .wmde-markdown h2, & .wmde-markdown h3, & .wmde-markdown h4, & .wmde-markdown h5, & .wmde-markdown h6': {
    color: '#111827 !important',
    backgroundColor: 'transparent !important',
  },
  
  '& .wmde-markdown p, & .wmde-markdown li, & .wmde-markdown span, & .wmde-markdown div': {
    color: '#374151 !important',
    backgroundColor: 'transparent !important',
  },

  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: theme.spacing(3),
    border: '1px solid #E5E7EB',
    fontSize: '1rem',
    borderRadius: theme.spacing(1.5),
    overflow: 'hidden',

    [theme.breakpoints.up('md')]: {
      display: 'table',
      fontSize: '1rem',
      tableLayout: 'fixed',
      '& td, & th': {
        wordWrap: 'break-word',
      },
    },

    [theme.breakpoints.down('md')]: {
      display: 'block',
      border: 'none',
      '& thead': {
        display: 'none',
      },
      '& tbody': {
        display: 'block',
        width: '100%',
      },
      '& tr': {
        display: 'block',
        width: '100%',
        marginBottom: theme.spacing(2),
        background: '#FEFEFE',
        borderRadius: theme.spacing(2),
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        border: '1px solid #E5E7EB',
      },
      '& td': {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        padding: theme.spacing(2),
        borderBottom: '1px solid #F3F4F6',
        fontSize: '0.9rem',
        '&:last-child': {
          borderBottom: 'none',
        },
        '&::before': {
          content: 'attr(data-label)',
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          color: '#6B7280',
          letterSpacing: '0.05em',
          marginBottom: theme.spacing(0.5),
        },
      },
    },

    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
      '& td': {
        padding: theme.spacing(1.5),
        fontSize: '0.875rem',
      },
    },
  },

  '& th, & td': {
    padding: theme.spacing(2),
    textAlign: 'left',
    verticalAlign: 'top',
    [theme.breakpoints.up('md')]: {
      borderBottom: '1px solid #F3F4F6',
      borderRight: '1px solid #F3F4F6',
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },

  '& th': {
    background: '#F9FAFB',
    fontWeight: 600,
    color: '#111827',
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.up('md')]: {
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },

  '& td[data-label="ID"]': {
    [theme.breakpoints.down('md')]: {
      background: 'rgba(94, 106, 210, 0.04)',
    },
  },

  '& td[data-label="Status"]': {
    [theme.breakpoints.down('md')]: {
      '&::before': {
        marginBottom: theme.spacing(0.5),
      },
    },
  },
}));

const extractFramesFromVideo = (
  videoFile: File,
  maxFrames: number = 60,
  onProgress?: (progress: { current: number; total: number }) => void,
  t?: TFunction
): Promise<Blob[]> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const frames: Blob[] = [];
    let isProcessing = false;
    let hasStarted = false;

    // Enhanced settings for better device compatibility
    video.autoplay = false;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    
    // Create object URL for the video file
    const videoUrl = URL.createObjectURL(videoFile);
    video.src = videoUrl;

    // Cleanup function to prevent memory leaks
    const cleanup = () => {
      try {
        URL.revokeObjectURL(videoUrl);
        video.removeAttribute('src');
        video.load();
        clearTimeout(timeoutId);
        clearTimeout(metadataTimeoutId);
      } catch (cleanupError) {
        console.warn('Cleanup error:', cleanupError);
      }
    };

    // Enhanced error handling for different video formats and devices
    const handleVideoError = (error: any, context: string) => {
      console.error(`Video error in ${context}:`, error);
      cleanup();
      
      const message = error?.message || 'Unknown error';

      if (error?.code === 4 || message.includes('not supported')) {
        reject(new Error(t ? t('upload.errors.unsupportedFormat') : 'Video format not supported. Please try MP4 format.'));
      } else if (error?.code === 2) {
        reject(new Error(t ? t('upload.errors.networkError') : 'Network error loading video. Please check your connection.'));
      } else if (error?.code === 3) {
        reject(new Error(t ? t('upload.errors.videoCorrupted') : 'Video file appears to be corrupted. Please try a different file.'));
      } else {
        reject(new Error(t ? t('upload.errors.processingFailed', { message }) : `Failed to process video: ${message}`));
      }
    };

    video.onloadedmetadata = async () => {
      hasStarted = true;
      
      if (!ctx) {
        cleanup();
        reject(new Error(t ? t('upload.errors.canvasContext') : 'Failed to get canvas context. Your browser may not support this feature.'));
        return;
      }

      // Validate video properties
      if (!video.duration || video.duration === Infinity || isNaN(video.duration)) {
        cleanup();
        reject(new Error(t ? t('upload.errors.invalidDuration') : 'Invalid video duration. Please try a different video file.'));
        return;
      }

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        cleanup();
        reject(new Error(t ? t('upload.errors.invalidDimensions') : 'Invalid video dimensions. Please try a different video file.'));
        return;
      }

      // Set canvas dimensions with better aspect ratio handling
      const maxWidth = 640;
      const aspectRatio = video.videoWidth / video.videoHeight;
      
      if (aspectRatio > 1) {
        canvas.width = maxWidth;
        canvas.height = Math.round(maxWidth / aspectRatio);
      } else {
        canvas.height = maxWidth;
        canvas.width = Math.round(maxWidth * aspectRatio);
      }

      const duration = video.duration;
      
      // ADAPTIVE FRAME RATE with intelligent limits
      const frameInterval = 0.5; // seconds between frames
      let totalFramesToExtract = Math.min(maxFrames, Math.floor(duration / frameInterval) + 1);
      
      // For very long videos, use intelligent sampling
      if (duration > 60) {
        console.log(`Long video detected (${duration.toFixed(1)}s), using optimized sampling...`);
        totalFramesToExtract = Math.min(30, totalFramesToExtract); // Cap at 30 frames for long videos
      }
      
      console.log(`Video: ${duration.toFixed(2)}s, ${video.videoWidth}x${video.videoHeight}, extracting ${totalFramesToExtract} frames`);
      
      onProgress?.({ current: 0, total: totalFramesToExtract });

      const captureFrame = async (frameIndex: number): Promise<void> => {
        if (frameIndex >= totalFramesToExtract || !ctx) {
          cleanup();
          resolve(frames);
          return;
        }

        if (isProcessing) return;
        isProcessing = true;

        try {
          // Calculate time for this frame with better distribution for long videos
          let currentTime;
          if (duration > 60) {
            // For long videos, distribute frames more evenly across the entire duration
            currentTime = (frameIndex / (totalFramesToExtract - 1)) * (duration - 0.5);
          } else {
            // For shorter videos, use regular interval
            currentTime = frameIndex * frameInterval;
          }
          
          // Ensure we don't exceed video duration
          currentTime = Math.min(currentTime, duration - 0.1);
          
          // Enhanced frame seeking for better device compatibility
          video.currentTime = currentTime;
          
          // Wait for seek to complete
          await new Promise((seekResolve, seekReject) => {
            const seekTimeout = setTimeout(() => {
              seekReject(new Error(t ? t('upload.errors.seekTimeout') : 'Seek timeout'));
            }, 2000);
            
            const onSeeked = () => {
              clearTimeout(seekTimeout);
              video.removeEventListener('seeked', onSeeked);
              video.removeEventListener('error', onSeekError);
              seekResolve(undefined);
            };
            
            const onSeekError = (e: any) => {
              clearTimeout(seekTimeout);
              video.removeEventListener('seeked', onSeeked);
              video.removeEventListener('error', onSeekError);
              seekReject(e);
            };
            
            video.addEventListener('seeked', onSeeked);
            video.addEventListener('error', onSeekError);
          });

          // Enhanced frame capture with retry logic
          let captureAttempts = 0;
          const maxCaptureAttempts = 3;
          
          while (captureAttempts < maxCaptureAttempts) {
            try {
              // Clear canvas before drawing
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              
              // Draw the video frame
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

              const blob = await new Promise<Blob | null>((blobResolve) => {
                canvas.toBlob(
                  (b) => blobResolve(b),
                  'image/jpeg',
                  0.8 // Slightly higher quality for better analysis
                );
              });

              if (blob && blob.size > 0) {
                frames.push(blob);
                console.log(`Captured frame ${frameIndex + 1}/${totalFramesToExtract} at ${currentTime.toFixed(1)}s (${blob.size} bytes)`);
                onProgress?.({ current: frameIndex + 1, total: totalFramesToExtract });
                break; // Success, exit retry loop
              } else {
                throw new Error('Empty blob generated');
              }
            } catch (captureError) {
              captureAttempts++;
              console.warn(`Frame capture attempt ${captureAttempts} failed:`, captureError);
              
              if (captureAttempts >= maxCaptureAttempts) {
                console.warn(`Failed to capture frame ${frameIndex + 1} after ${maxCaptureAttempts} attempts, skipping...`);
                break; // Skip this frame and continue
              }
              
              // Wait before retry
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }

          isProcessing = false;
          
          // Small delay before next frame to prevent overwhelming the browser
          await new Promise(resolve => setTimeout(resolve, 10));
          
          await captureFrame(frameIndex + 1);
        } catch (error) {
          console.error('Frame processing error:', error);
          isProcessing = false;
          
          // More resilient error handling - continue with next frame instead of failing
          if (frames.length > 0) {
            console.warn(`Continuing with ${frames.length} frames captured so far...`);
            await captureFrame(frameIndex + 1);
          } else {
            // If no frames captured yet, try once more
            if (frameIndex === 0) {
              await new Promise(resolve => setTimeout(resolve, 200));
              await captureFrame(frameIndex);
            } else {
              cleanup();
              resolve(frames); // Return what we have
            }
          }
        }
      };

      // Start capture process
      try {
        await captureFrame(0);
      } catch (error) {
        handleVideoError(error, 'frame capture initialization');
      }
    };

    video.onerror = (e) => {
      if (!hasStarted) {
        handleVideoError(video.error || e, 'video loading');
      }
    };

    // Enhanced timeout handling
    const timeoutId = setTimeout(() => {
      if (frames.length === 0) {
        cleanup();
        reject(new Error(t ? t('upload.errors.processingTimeout') : 'Video processing timed out. Please try a shorter video or different format.'));
      } else {
        console.warn(`Processing timed out, returning ${frames.length} frames captured so far.`);
        cleanup();
        resolve(frames);
      }
    }, 90000); // 90 second timeout

    // Metadata loading timeout
    const metadataTimeoutId = setTimeout(() => {
      if (!hasStarted) {
        cleanup();
        reject(new Error(t ? t('upload.errors.metadataTimeout') : 'Video metadata loading timed out. Please check the video file.'));
      }
    }, 10000); // 10 second timeout for metadata
  });
};

const Upload = () => {
  // Set page title for analytics
  usePageTitle('Upload Video');

  // Upload page automatically scrolls to top via global scroll restoration

  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  
  // Upload and analysis state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<'idle' | 'processing' | 'analyzing' | 'generating' | 'complete'>('idle');
  const [error, setError] = useState<string | null>(null);
  
  // Report state
  const [teaserReport, setTeaserReport] = useState<any>(null);
  const [fullReport, setFullReport] = useState<any>(null);
  const [reportId, setReportId] = useState<string | null>(null);

  // Check for unlocked state from URL parameters and sessionStorage
  useEffect(() => {
    const urlReportId = searchParams.get('report_id');
    if (urlReportId) {
      // Check if this specific report is unlocked using the new format
      const isUnlocked = sessionStorage.getItem(`report_unlocked_${urlReportId}`) === 'true';
      
      // Try to load report data from sessionStorage if available
      const storedReportData = sessionStorage.getItem(`report_data_${urlReportId}`);
      if (storedReportData) {
        try {
          const parsedData = JSON.parse(storedReportData);
          setTeaserReport(parsedData.teaserReport);
          setFullReport(parsedData.fullReport);
          setReportId(urlReportId);
          setCurrentPhase('complete');
          
          if (isUnlocked) {
            // setIsPaid(true); // Removed as per edit hint
          }
        } catch (error) {
          console.error('Error parsing stored report data:', error);
        }
      } else {
        // Also check the new format used by PaymentSuccessPage
        const newFormatData = sessionStorage.getItem(urlReportId);
        if (newFormatData) {
          try {
            const parsedData = JSON.parse(newFormatData);
            if (parsedData.isUnlocked) {
              // If report is unlocked in new format, show full report directly
              setTeaserReport(parsedData.teaser_content);
              setFullReport(parsedData.full_content);
              setReportId(urlReportId);
              setCurrentPhase('complete');
              // setIsPaid(true); // Removed as per edit hint
            }
          } catch (error) {
            console.error('Error parsing new format stored report data:', error);
          }
        }
      }
    }
  }, [searchParams, reportId]);

  // Reset all state when component unmounts
  useEffect(() => {
    return () => {
      setIsUploading(false);
      setUploadProgress(0);
      setCurrentPhase('idle');
      setError(null);
      setTeaserReport(null);
      setFullReport(null);
      setReportId(null);
      // setIsPaid(false); // Removed as per edit hint
    };
  }, []);

  // Cleanup old report data from sessionStorage (keep only last 5 reports)
  useEffect(() => {
    const cleanupOldReports = () => {
      const reportKeys = Object.keys(sessionStorage).filter(key => key.startsWith('report_data_'));
      if (reportKeys.length > 5) {
        // Sort by timestamp and remove oldest reports
        const reportsWithTimestamps = reportKeys.map(key => {
          try {
            const data = JSON.parse(sessionStorage.getItem(key) || '{}');
            return { key, timestamp: data.timestamp || 0 };
          } catch {
            return { key, timestamp: 0 };
          }
        }).sort((a, b) => b.timestamp - a.timestamp);
        
        // Remove oldest reports (keep only first 5)
        reportsWithTimestamps.slice(5).forEach(({ key }) => {
          sessionStorage.removeItem(key);
        });
      }
    };
    
    cleanupOldReports();
  }, []);

  // Get user-friendly status message based on current phase
  const getStatusMessage = () => {
    switch (currentPhase) {
      case 'processing':
        return t('upload.progress.processing');
      case 'analyzing':
        return t('upload.progress.analyzing');
      case 'generating':
        return t('upload.progress.creating');
      case 'complete':
        return t('upload.progress.done');
      default:
        return '';
    }
  };

  const handleUpload = async (acceptedFiles: File[]) => {
    const videoFile = acceptedFiles[0];
    if (!videoFile) return;

    // Type check for video files
    if (!videoFile.type.startsWith('video/')) {
        setError(t('upload.errors.invalidFileType'));
        return;
    }
    
    // Initialize upload process
    setIsUploading(true);
    setError(null);
    setTeaserReport(null);
    setFullReport(null);
    setReportId(null);
    setUploadProgress(0);
    setCurrentPhase('processing');

    // Helper function for smooth progress updates
    let currentProgress = 0;
    const smoothProgressUpdate = (targetProgress: number, duration: number = 1000) => {
      return new Promise<void>((resolve) => {
        const startProgress = currentProgress;
        const progressDiff = targetProgress - startProgress;
        const steps = Math.max(20, Math.abs(progressDiff * 2)); // More steps for smoother animation
        const stepSize = progressDiff / steps;
        const stepDuration = duration / steps;
        
        let currentStep = 0;
        const interval = setInterval(() => {
          currentStep++;
          const newProgress = startProgress + (stepSize * currentStep);
          
          if (currentStep >= steps) {
            currentProgress = targetProgress;
            setUploadProgress(targetProgress);
            clearInterval(interval);
            resolve();
          } else {
            currentProgress = newProgress;
            setUploadProgress(Math.round(newProgress));
          }
        }, stepDuration);
      });
    };

    try {
      console.log(`Processing video: ${videoFile.name} (${(videoFile.size / 1024 / 1024).toFixed(2)}MB, ${videoFile.type})`);
      
      // Phase 1: Processing video (0-35%)
      setCurrentPhase('processing');
      await smoothProgressUpdate(5, 300);
      
      const frames = await extractFramesFromVideo(
        videoFile, 
        60, // Max frames
        (progress) => {
          // Update progress during video processing (5-30%)
          const progressPercent = 5 + (progress.current / progress.total) * 25;
          currentProgress = progressPercent;
          setUploadProgress(Math.round(progressPercent));
        },
        t // Pass translation function
      );
      
      await smoothProgressUpdate(35, 500);
      console.log(`Successfully extracted ${frames.length} frames, uploading for analysis...`);
      
      // Phase 2: Preparing for analysis (35-45%)
      setCurrentPhase('analyzing');
      await smoothProgressUpdate(40, 800);
      
      const formData = new FormData();
      frames.forEach((frame) => {
        formData.append('frame', frame, 'frame.jpg');
      });

      // Append language to the form data
      formData.append('language', i18n.language);
      await smoothProgressUpdate(45, 500);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        setError(t('upload.errors.analysisTimeout'));
      }, 90000); // 90-second timeout

      // Phase 3: AI Analysis with fake progressive updates
      const startTime = Date.now();
      const maxWaitTime = 75000; // 75 seconds (a bit less than the 90s timeout)

      // Start fake progress updater that runs in parallel with API call
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progressRatio = Math.min(elapsed / maxWaitTime, 1); // 0 to 1
        const fakeProgress = 45 + (progressRatio * 35); // 45% to 80%
        currentProgress = fakeProgress;
        setUploadProgress(Math.round(fakeProgress));
      }, 800); // Update every 800ms for smooth progress

      const response = await fetch('/api/analyze', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      clearInterval(progressInterval); // Stop fake progress immediately when response arrives
      
      // Response received - jump to 85% (or stay at current fake progress if higher)
      currentProgress = Math.max(85, currentProgress);
      setUploadProgress(Math.round(currentProgress));

      if (!response.ok) {
        let errorMessage;
        const contentType = response.headers.get('content-type');
        try {
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || `HTTP error! status: ${response.status}`;
          } else {
            errorMessage = await response.text() || `HTTP error! status: ${response.status}`;
          }
        } catch (e) {
          errorMessage = `HTTP error! status: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      // Phase 4: Generating report (85-100%) - no artificial delays
      setCurrentPhase('generating');
      await smoothProgressUpdate(90, 100); // Quick transition

      const result = await response.json();
      await smoothProgressUpdate(95, 100); // Quick transition

      if (!result.teaser_content || !result.full_content) {
        console.error('Invalid report structure received:', result);
        throw new Error(t('upload.errors.analysisIncomplete'));
      }

      // Complete the process
      setCurrentPhase('complete');
      await smoothProgressUpdate(100, 200); // Quick completion
      
      // No artificial wait - proceed immediately
      setTeaserReport(result.teaser_content);
      setFullReport(result.full_content);
      setReportId(result.reportId);
      // Don't automatically set as paid - let the user go through the alpha overlay flow
      // setIsPaid(false); // Removed as per edit hint
      
      // Store report data in sessionStorage for later retrieval
      const reportData = {
        teaserReport: result.teaser_content,
        fullReport: result.full_content,
        reportId: result.reportId,
        timestamp: Date.now()
      };
      sessionStorage.setItem(`report_data_${result.reportId}`, JSON.stringify(reportData));
      
      // Auto-scroll to teaser report after upload completes
      setTimeout(() => {
        const paywallElement = document.querySelector('[data-testid="report-paywall"]') as HTMLElement;
        if (paywallElement) {
          paywallElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        } else {
          // Fallback: scroll to bottom of page to show any new content
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 800);

    } catch (err: any) {
      console.error("Upload or processing failed:", err);
      if (err.name === 'AbortError') {
        // The error is already set by the timeout handler
      } else {
        setError(err.message || t('upload.errors.unknownProcessingError'));
      }
      setCurrentPhase('idle');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleUpload,
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.webm'],
    },
    multiple: false,
  });

  // Removed handlePaymentSuccess, handleDownloadPdf, and ReportPaywall

  const handleDownloadPdf = () => {
    try {
      window.print();
    } catch (e) {
      console.error('Failed to trigger print:', e);
    }
  };
 
  return (
    <UploadSection>
      <Container maxWidth="lg">
        <Typography 
          variant="h1" 
          component="h1" 
          align="center" 
          gutterBottom
          sx={{
            fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
            mb: { xs: 3, sm: 4, md: 5 },
            fontWeight: 700,
            background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('upload.title')}
        </Typography>
        <Typography 
          variant="h2" 
          component="h2" 
          align="center" 
          paragraph
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
            maxWidth: { xs: '100%', sm: '800px', lg: '1000px' },
            mx: 'auto',
            mb: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 0 },
            color: '#6B7280',
            lineHeight: 1.6,
          }}
        >
          {t('upload.subtitle')}
        </Typography>
        <Typography 
          variant="body1" 
          align="center" 
          paragraph
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem', lg: '1.1rem' },
            maxWidth: { xs: '100%', sm: '600px', lg: '800px' },
            mx: 'auto',
            mb: { xs: 3, sm: 4, md: 5 },
            px: { xs: 2, sm: 0 },
            color: '#9CA3AF',
            lineHeight: 1.6,
          }}
        >
          {t('upload.instructions')}
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: { xs: 3, sm: 4 },
              mx: 'auto',
              maxWidth: { xs: '100%', sm: '600px', md: '800px' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              background: 'rgba(239, 68, 68, 0.04)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: '#DC2626',
              },
              color: '#7F1D1D',
            }}
          >
            {error}
          </Alert>
        )}

        <Box mt={{ xs: 3, sm: 4, md: 5 }}>
          {/* Only show drag & drop area if no upload has started and no report exists */}
          {!isUploading && !teaserReport && (
            <DropzoneContainer 
              {...getRootProps()}
              className={isDragActive ? 'drag-active' : ''}
            >
              <input {...getInputProps()} aria-label="Upload workplace video file" />
              <CloudUploadIcon 
                className="upload-icon"
                sx={{ 
                  fontSize: '3rem', 
                  color: '#9CA3AF',
                  mb: 3,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }} 
                aria-hidden="true"
              />
              <Typography 
                className="upload-text" 
                variant="h2" 
                component="h2" 
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#111827',
                  mb: 2,
                }}
              >
                {isDragActive
                  ? t('upload.dropzone.activeText')
                  : t('upload.dropzone.inactiveText')
                }
              </Typography>
              <Typography 
                variant="body1" 
                sx={{
                  color: '#6B7280',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                }}
              >
                {t('upload.dropzone.formats')}
              </Typography>
            </DropzoneContainer>
          )}
          
          <AnimatePresence>
            {(isUploading) && (
              <ProgressContainer
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    <Typography 
                      variant="h3" 
                      component="h3"
                      align="center"
                      sx={{
                        color: '#111827',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '1.75rem' },
                        mb: 1,
                      }}
                    >
                      {getStatusMessage()}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      align="center"
                      sx={{
                        color: '#6B7280',
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        mb: 4,
                        lineHeight: 1.6,
                      }}
                    >
                                      {currentPhase === 'analyzing' && t('upload.progress.analyzingDescription')}
                {currentPhase === 'processing' && t('upload.progress.processingDescription')}
                {currentPhase === 'generating' && t('upload.progress.generatingDescription')}
                {currentPhase === 'complete' && t('upload.progress.completeDescription')}
                    </Typography>
                  </motion.div>
                  
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    style={{ transformOrigin: 'left center' }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadProgress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: 'rgba(94, 106, 210, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            background: 'linear-gradient(90deg, #5E6AD2 0%, #8B5CF6 100%)',
                            transition: 'transform 0.3s ease-in-out',
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#9CA3AF',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                                        {currentPhase === 'processing' && t('upload.progress.processing')}
                {currentPhase === 'analyzing' && t('upload.progress.analyzing')}
                {currentPhase === 'generating' && t('upload.progress.generating')}
                {currentPhase === 'complete' && t('upload.progress.complete')}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#5E6AD2',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                        }}
                      >
                        {uploadProgress}%
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              </ProgressContainer>
            )}
          </AnimatePresence>
        </Box>

        {/* Removed ReportPaywall */}

        {fullReport && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <DownloadButton
              startIcon={<GetAppIcon />}
              onClick={handleDownloadPdf}
              className="no-print"
            >
              {t('upload.downloadButton')}
            </DownloadButton>
            
            <ReportContainer id="report-content" className="wmde-markdown-var">
              <ReportRenderer report={fullReport} />
            </ReportContainer>
          </motion.div>
        )}

        {/* Removed PaymentModal */}
      </Container>
    </UploadSection>
  );
};

export default Upload; 